import { useState, useCallback, useEffect, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import { getErrorBoundary } from 'FrontRoyalErrorBoundary';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import { SendbirdSdkProvider } from 'SendbirdSdkProvider';
import { useMessagingConfig } from 'Messaging/utils/useMessagingConfig';
import { MessagingProvider, useMessagingSurface } from 'Messaging/components/MessagingProvider';
import { ChannelList } from 'Messaging/components/ChannelList';
import { Channel } from 'Messaging/components/Channel';
import { ChannelBuilder } from 'Messaging/components/ChannelBuilder';
import { ErrorMessage } from 'Messaging/components/ErrorMessage';
import { getActiveChannelUrl, getLoading, getShowNewMessage } from 'Messaging/selectors';
import { usePollForUsers } from 'Messaging/utils';
import { useNetworkOutletContext } from '../StudentNetworkV2';

// Must match Tailwind `md` breakpoint (992px) used by StudentNetworkV2's
// desktop/mobile CSS containers (`hidden md:flex` / `md:hidden`).
const MD_BREAKPOINT = 992;

function useIsMobileLayout() {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < MD_BREAKPOINT);

    useEffect(() => {
        const mql = window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    return isMobile;
}

/**
 * Ensure the standalone Sendbird SDK is connected before rendering channel
 * components. The UIKit's SendBirdProvider fires onConnected before channel
 * queries are reliable on first cold load. Explicitly connecting the standalone
 * SDK (the same approach openChatAndSelectUser uses) warms up the shared
 * connection so GroupChannelList and useGetUserChannelMap work immediately.
 *
 * On cold start the standalone SDK may not yet be initialized (Angular
 * bootstrap runs `initializeSendbirdSdk` asynchronously). This hook polls
 * for SDK availability so it can connect before we render the channel list.
 */
function useEnsureSdkConnected() {
    const { userId, accessToken } = useMessagingConfig();
    const [ready, setReady] = useState(() => {
        const sdk = SendbirdSdkProvider.sdk;
        return sdk?.connectionState === 'OPEN';
    });

    useEffect(() => {
        if (ready || !userId || !accessToken) return undefined;

        let cancelled = false;

        const tryConnect = () => {
            const sdk = SendbirdSdkProvider.sdk;
            if (!sdk) return false;
            if (sdk.connectionState === 'OPEN') {
                setReady(true);
                return true;
            }
            sdk.connect(userId, accessToken)
                .then(() => {
                    if (!cancelled) setReady(true);
                })
                .catch(() => {
                    if (!cancelled) setReady(true);
                });
            return true;
        };

        // If the SDK is already available, connect immediately
        if (tryConnect()) {
            return () => {
                cancelled = true;
            };
        }

        // Otherwise poll until Angular bootstrap initializes it (typically <1s)
        const id = setInterval(() => {
            if (tryConnect()) clearInterval(id);
        }, 200);

        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [userId, accessToken, ready]);

    return ready;
}

function MessagesContent() {
    const loading = useSelector(getLoading);
    const activeChannel = useSelector(getActiveChannelUrl);
    const showNewMessage = useSelector(getShowNewMessage);
    const { isMobile } = useIsMobileMixin();
    const isNetwork = useMessagingSurface() === 'student-network';
    const sdkReady = useEnsureSdkConnected();

    usePollForUsers();

    if (loading || !sdkReady) {
        return (
            <div className="flex h-full items-center justify-center">
                <FrontRoyalSpinner className="no-delay" color="force-brand" />
            </div>
        );
    }

    // On mobile network, only show ChannelBuilder when explicitly composing a new
    // message. Otherwise the ChannelList takes full width and the Suspense fallback
    // for ChannelBuilder gets crammed beside it, producing two competing spinners.
    // On desktop, show the builder whenever no channel is selected (default compose state).
    const showBuilder = isMobile && isNetwork ? showNewMessage : !activeChannel || showNewMessage;

    return (
        <ErrorBoundary FallbackComponent={getErrorBoundary({ other: ErrorMessage })}>
            <div className="flex h-full">
                <ChannelList />
                {showBuilder ? (
                    <Suspense fallback={<FrontRoyalSpinner className="no-delay" color="force-brand" />}>
                        <ChannelBuilder />
                    </Suspense>
                ) : activeChannel ? (
                    <Channel />
                ) : null}
            </div>
        </ErrorBoundary>
    );
}

export function MessagesView() {
    const { variant } = useNetworkOutletContext();
    const isMobileLayout = useIsMobileLayout();
    const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
    const containerRef = useCallback((node: HTMLDivElement | null) => setContainerEl(node), []);

    // Both desktop and mobile outlets are always mounted in React (just CSS-hidden).
    // Skip rendering heavy Sendbird components in the inactive outlet to avoid
    // dual GroupChannelList instances conflicting at the SDK level.
    // Uses 992px (Tailwind `md`) to match the CSS container visibility breakpoints.
    const isActiveOutlet = variant === 'mobile' ? isMobileLayout : !isMobileLayout;
    if (!isActiveOutlet) return null;

    return (
        <div className="network-messages-surface h-full w-full max-w-[1100px] bg-white @container md:h-auto md:overflow-hidden md:rounded-8 md:border md:border-solid md:border-slate-grey-200 md:shadow">
            <div ref={containerRef} className="relative h-full overflow-hidden md:h-[700px] md:min-h-[500px]">
                <MessagingProvider surface="student-network" modalContainer={containerEl}>
                    <MessagesContent />
                </MessagingProvider>
            </div>
        </div>
    );
}
